import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Search as SearchIcon,
  Star as StarIcon,
  LocalBar as LocalBarIcon,
  LocalCafe as LocalCafeIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

// Extend window type to include RecommendationWidget
declare global {
  interface Window {
    RecommendationWidget: any;
  }
}

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { setMode, clearAll } = useRecipe();

  useEffect(() => {
    // Initialize the recommendation widget
    const initWidget = () => {
      if (window.RecommendationWidget) {
        new window.RecommendationWidget('recommendation-widget', {
          siteId: '17',
          categories: 'Lifestyle,AI',
          layout: 'carousel',
          theme: 'light',
          limit: 10,
          showImages: true,
          showDescription: true,
          showTags: false,
          showTitle: true,
          title: 'You might also like...',
          imageSize: '50px',
          imageShape: 'rounded',
          autoRotate: true,
          openInNewTab: true,
          animation: 'fade',
          hoverEffect: 'scale',
          columns: 2,
          descriptionLength: 50
        });
      }
    };
    
    // Delay initialization to ensure script is loaded
    const timer = setTimeout(initWidget, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Create A Dish - AI-Powered Recipe Generator | Personalized Meal Planning</title>
        <meta name="description" content="Discover delicious recipes tailored to your preferences with our AI-powered recipe generator. Create personalized meals, explore dietary options, and cook with confidence. Made for food lovers everywhere." />
        <meta name="keywords" content="recipe generator, AI recipes, personalized cooking, meal planning, dietary recipes, cooking assistant, food generator, recipe ideas, AI cooking, custom recipes" />
        <link rel="canonical" href="https://createadish.com/" />
        <meta property="og:title" content="Create A Dish - AI-Powered Recipe Generator" />
        <meta property="og:description" content="Discover delicious recipes tailored to your preferences with our AI-powered recipe generator. Create personalized meals and cook with confidence." />
        <meta property="og:url" content="https://createadish.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Create A Dish - AI-Powered Recipe Generator" />
        <meta name="twitter:description" content="Discover delicious recipes tailored to your preferences with our AI-powered recipe generator." />
      </Helmet>
    <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '50vh', md: '60vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Create A Dish
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Pick from Meal Types, Cuisines, Ingredients, and More!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              maxWidth: '600px',
            }}
          >
            Choose from 8 categories to create your perfect dish in seconds.
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 2, md: 4 },
          p: { xs: 1, md: 2 },
        }}
      >
        <Button
          variant="contained"
          size="large"
          startIcon={<RestaurantIcon />}
          onClick={() => {
            setMode('generate');
            navigate('/composer');
          }}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            fontSize: '1.1rem',
            py: { xs: 1, md: 1.5 },
            color: 'white',
          }}
        >
          Create My Recipe
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<SearchIcon />}
          onClick={() => {
            setMode('find');
            navigate('/composer');
          }}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            fontSize: '1.1rem',
            py: { xs: 1, md: 1.5 },
            color: 'white',
          }}
        >
          Find a Recipe
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<StarIcon />}
          onClick={() => {
            clearAll();
            setMode('popular');
            navigate('/popular');
          }}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            fontSize: '1.1rem',
            py: { xs: 1, md: 1.5 },
            color: 'white',
          }}
        >
          Popular Recipes
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<LocalBarIcon />}
          onClick={() => navigate('/drinks')}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            fontSize: '1.1rem',
            py: { xs: 1, md: 1.5 },
            color: 'white',
          }}
        >
          Explore Drinks
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<LocalCafeIcon />}
          onClick={() => navigate('/dangers-of-coffee')}
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            fontSize: '1.1rem',
            py: { xs: 1, md: 1.5 },
            color: 'white',
          }}
        >
          Dangers of Coffee
        </Button>
      </Box>

      {/* Recommendation Widget Section */}
      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          mb: { xs: 2, md: 3 },
          px: { xs: 2, md: 3 },
          maxWidth: '500px',
          mx: 'auto',
        }}
      >
        <div 
          id="recommendation-widget" 
          data-rec-widget
          style={{ minHeight: '200px' }}
        />
      </Box>
    </Box>
    </>
  );
};

export default Landing;